import { useContext, useEffect, useState } from 'react';

import {
  QBox,
  QButton,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelect,
  QSelectItem,
  QSelectPlaceholder,
  QText,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { DocumentCategory, QualioDocument } from '../../../api/model/document';
import { MedtechUserV2 } from '../../../api/user';
import { sortByLabel } from '../../../util/SelectionOptionUtils';
import { DocumentOverviewContext } from '../DocumentOverview/Context';

type ChangeDocumentOwnerModalProps = {
  isLoading?: boolean;
  isModalOpen: boolean;
  modalDescription: string;
  users: MedtechUserV2[];
  onClose: () => void;
  handleChangeOwner: (ownerId: number) => Promise<any>;
  onSuccess: (data: any) => void;
  onError?: () => void;
};

const getPotentialOwnerOptions = (
  users: MedtechUserV2[],
  qualioDocument?: QualioDocument,
) => {
  return users
    .filter((user) => {
      if (
        qualioDocument &&
        qualioDocument.category === DocumentCategory.ISSUE_RELATED
      ) {
        return user.permissions.includes('can_work_on_issue');
      }
      return user.permissions.includes('can_author_doc');
    })
    .map(({ id, full_name }) => ({
      value: id.toString(),
      label: full_name,
    }))
    .sort(sortByLabel);
};

export const ChangeDocumentOwnerModal = ({
  isLoading,
  isModalOpen,
  modalDescription,
  users,
  onClose,
  handleChangeOwner,
  onSuccess,
  onError,
}: ChangeDocumentOwnerModalProps) => {
  const { t } = useTranslation();
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>();
  const [isChangingOwner, setIsChangingOwner] = useState(false);
  const { qualioDocument } = useContext(DocumentOverviewContext);

  const potentialOwnerOptions: QSelectItem[] = getPotentialOwnerOptions(
    users,
    qualioDocument,
  );

  useEffect(() => {
    setSelectedOwnerId(undefined);
  }, [isModalOpen]);

  const onSubmit = async () => {
    if (!selectedOwnerId) {
      return;
    }
    setIsChangingOwner(true);
    try {
      const data = await handleChangeOwner(+selectedOwnerId);
      setIsChangingOwner(false);
      onSuccess(data);
    } catch (error) {
      setIsChangingOwner(false);
      if (onError) {
        onError();
      }
    }
  };

  return (
    <>
      {isModalOpen && (
        <QModal isOpen onClose={onClose}>
          <QModalHeader>
            <QText>{t('buttons.changeOwner')}</QText>
          </QModalHeader>
          <QModalBody>
            <QText>{modalDescription}</QText>
            <QText marginTop={5} marginBottom={1} weight="semibold">
              {t('labels.newOwner')}
            </QText>
            <QBox data-cy="change-document-owner-select">
              <QSelect
                aria-label={t('accessibilityLabels.selectNewOwnerAriaLabel')}
                size="sm"
                options={potentialOwnerOptions}
                onChange={(value) => setSelectedOwnerId(value?.value)}
                value={selectedOwnerId}
                isDisabled={isLoading}
                isLoading={isLoading}
              >
                <QSelectPlaceholder>
                  <QText>{t('forms.selectNewOwner')}</QText>
                </QSelectPlaceholder>
              </QSelect>
            </QBox>
          </QModalBody>
          <QModalActions>
            <QButton
              isDisabled={isChangingOwner}
              variant="outline"
              onClick={onClose}
            >
              {t('buttons.cancel')}
            </QButton>
            <QButton
              isLoading={isChangingOwner}
              isDisabled={!selectedOwnerId}
              onClick={onSubmit}
            >
              {t('buttons.applyChanges')}
            </QButton>
          </QModalActions>
        </QModal>
      )}
    </>
  );
};
